/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx } from "@emotion/core";
import { Formik, Field, FieldArray, getIn } from "formik";
import { Redirect } from "react-router";
import _last from "lodash/last";
import _get from "lodash/get";
import _includes from "lodash/includes";

import { get, post } from "../api";

import PrimaryCTAButton from "../../components/PrimaryCTA";
import Form from "../../components/Form";
import FormField from "../../components/FormField";
import Input, { SearchableDropdown } from "../../components/Input";
import ValidationError from "../../components/ValidationError";
import Label from "../../components/Label";
import RemoveButton from "../../components/RemoveButton";
import RadioButton from "../../components/RadioButton";
import RadioGroup from "../../components/RadioGroup";
import HorizontalDivider from "../../components/HorizontalDivider";

import PageContainer from "../components/PageContainer";
import { resourceUrls } from "../../config";
import { handleGet, handlePost } from "../api/handler";

const emptyQualification = {
    level: null,
    subject: null,
    institution: null,
    grade: null,
    yearOfQualification: null,
};

const initialValues = {
    qualifications: [emptyQualification],
    hasAdditionalQualifications: null,
};

const hasEmptyField = qualification => _includes(qualification, null);

const levelValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a qualification type.";
    }
    return error;
};

const subjectValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a title.";
    }
    return error;
};

const institutionValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a name of institution.";
    }
    return error;
};

const gradeValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a grade.";
    }
    return error;
};

const yearOfQualificationValidator = value => {
    let error;
    if (!value) {
        error = "Please enter a year of completion.";
    } else if (
        // regex to test for a string of 4 digits
        !/^\d{4}$/.test(value)
    ) {
        error = "Must be a 4-digit year";
    }
    return error;
};

const hasAdditionalQualificationsValidator = value => {
    let error;
    if (!value) {
        error = "Please select yes or no.";
    }
    return error;
};

const getAdditionalQualificationsTypes = async ({
    setIsLoading,
    setIsLoggedIn,
    setError,
    setQualificationTypes,
}) => {
    await handleGet({
        getApiCall: () =>
            get({
                url:
                    resourceUrls.additionalQualifications
                        .getAdditionalQualifications,
            }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        setData: setQualificationTypes,
    });
};

const buildQualificationsBody = (
    { values: { hasAdditionalQualifications, qualifications } } = {
        values: { hasAdditionalQualifications: "no", qualifications: [] },
    }
) => {
    const additionalQuals = qualifications.map(qualification => ({
        grade: qualification.grade,
        institution: qualification.institution,
        qualification: _get(qualification, "level.value"),
        subject: qualification.subject,
        yearOfQualification: qualification.yearOfQualification,
    }));

    const body = {
        tutorQualifications:
            hasAdditionalQualifications === "yes" ? additionalQuals : [],
        applicationStep: "ADDITIONAL_QUALIFICATION",
    };

    return body;
};

const submitAdditionalQualification = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async (values, actions) => {
    const body = buildQualificationsBody({ values });

    await handlePost({
        postApiCall: () =>
            post({
                url: resourceUrls.additionalQualifications.submit,
                body: body,
            }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const AdditionalQualifications = () => {
    const [qualificationTypes, setQualificationTypes] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    useEffect(() => {
        getAdditionalQualificationsTypes({
            setIsLoading,
            setIsLoggedIn,
            setError,
            setQualificationTypes,
        });
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            isLoggedIn={isLoggedIn}
        >
            <h2>Do you have additional qualifications?</h2>
            <p>
                This could include university admissions tests, music grades,
                academic degrees etc.
            </p>
            <p>Please select an option:</p>

            <Formik
                initialValues={initialValues}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={submitAdditionalQualification(
                    setIsLoggedIn,
                    setError,
                    setFormSubmissionComplete
                )}
                render={({ isSubmitting, ...props}) => (
                    <Form onSubmit={props.handleSubmit}>
                        <RadioGroup>
                            <Field
                                validate={hasAdditionalQualificationsValidator}
                                component={RadioButton}
                                name="hasAdditionalQualifications"
                                id="yes"
                                data-testid="additionalQualificationsRadioYes"
                                label="Yes"
                            />
                        </RadioGroup>
                        {props.values.hasAdditionalQualifications === "yes" ? (
                            <FieldArray
                                name="qualifications"
                                render={arrayHelpers => (
                                    <>
                                        {props.values.qualifications.map(
                                            (qualification, index) => (
                                                <>
                                                    <Field
                                                        validate={
                                                            levelValidator
                                                        }
                                                        name={`qualifications.${index}.level`}
                                                        data-testid="level-add-qualifications"
                                                        render={({
                                                            field,
                                                            form,
                                                        }) => (
                                                            <FormField
                                                                key={`qualifications.${index}.level`}
                                                            >
                                                                <Label htmlFor="qualificationType">
                                                                    Qualification
                                                                    type
                                                                </Label>
                                                                <SearchableDropdown
                                                                    value={{
                                                                        label: _get(
                                                                            field,
                                                                            "value.label"
                                                                        ),
                                                                        value: _get(
                                                                            field,
                                                                            "value.value"
                                                                        ),
                                                                    }}
                                                                    options={Object.keys(
                                                                        qualificationTypes
                                                                    ).map(
                                                                        q => ({
                                                                            key: q,
                                                                            value: q,
                                                                            label:
                                                                                qualificationTypes[
                                                                                    q
                                                                                ],
                                                                        })
                                                                    )}
                                                                    error={
                                                                        props
                                                                            .touched
                                                                            .qualifications &&
                                                                        getIn(
                                                                            props.errors,
                                                                            `qualifications.${index}.level`
                                                                        )
                                                                    }
                                                                    onChange={option => {
                                                                        return form.setFieldValue(
                                                                            field.name,
                                                                            option
                                                                        );
                                                                    }}
                                                                    id="qualificationType"
                                                                />

                                                                {props.touched
                                                                    .qualifications &&
                                                                    getIn(
                                                                        props.errors,
                                                                        `qualifications.${index}.level`
                                                                    ) && (
                                                                        <ValidationError>
                                                                            {getIn(
                                                                                props.errors,
                                                                                `qualifications.${index}.level`
                                                                            )}
                                                                        </ValidationError>
                                                                    )}
                                                            </FormField>
                                                        )}
                                                    />
                                                    <Field
                                                        validate={
                                                            subjectValidator
                                                        }
                                                        name={`qualifications.${index}.subject`}
                                                        data-testid="subject-add-qualifications"
                                                        render={({
                                                            field,
                                                            form,
                                                        }) => (
                                                            <FormField
                                                                key={`qualifications.${index}.subject`}
                                                            >
                                                                <Label htmlFor="qualificationType">
                                                                    Title
                                                                </Label>
                                                                <Input
                                                                    type="text"
                                                                    onChange={
                                                                        props.handleChange
                                                                    }
                                                                    onBlur={
                                                                        props.handleBlur
                                                                    }
                                                                    value={
                                                                        props
                                                                            .values
                                                                            .qualifications[
                                                                            index
                                                                        ]
                                                                            .subject
                                                                    }
                                                                    name={`qualifications.${index}.subject`}
                                                                    id="courseTitle"
                                                                    error={
                                                                        props
                                                                            .touched
                                                                            .qualifications &&
                                                                        getIn(
                                                                            props.errors,
                                                                            `qualifications.${index}.subject`
                                                                        )
                                                                    }
                                                                />
                                                                {props.touched
                                                                    .qualifications &&
                                                                    getIn(
                                                                        props.errors,
                                                                        `qualifications.${index}.subject`
                                                                    ) && (
                                                                        <ValidationError>
                                                                            {getIn(
                                                                                props.errors,
                                                                                `qualifications.${index}.subject`
                                                                            )}
                                                                        </ValidationError>
                                                                    )}
                                                            </FormField>
                                                        )}
                                                    />
                                                    <Field
                                                        validate={
                                                            institutionValidator
                                                        }
                                                        name={`qualifications.${index}.institution`}
                                                        data-testid="institution-add-qualifications"
                                                        render={({
                                                            field,
                                                            form,
                                                        }) => (
                                                            <FormField
                                                                key={`qualifications.${index}.institution`}
                                                            >
                                                                <Label htmlFor="qualificationType">
                                                                    Name of
                                                                    institution
                                                                </Label>
                                                                <Input
                                                                    type="text"
                                                                    onChange={
                                                                        props.handleChange
                                                                    }
                                                                    onBlur={
                                                                        props.handleBlur
                                                                    }
                                                                    value={
                                                                        props
                                                                            .values
                                                                            .qualifications[
                                                                            index
                                                                        ]
                                                                            .institution
                                                                    }
                                                                    name={`qualifications.${index}.institution`}
                                                                    id="courseTitle"
                                                                    error={
                                                                        props
                                                                            .touched
                                                                            .qualifications &&
                                                                        getIn(
                                                                            props.errors,
                                                                            `qualifications.${index}.institution`
                                                                        )
                                                                    }
                                                                />

                                                                {props.touched
                                                                    .qualifications &&
                                                                    getIn(
                                                                        props.errors,
                                                                        `qualifications.${index}.institution`
                                                                    ) && (
                                                                        <ValidationError>
                                                                            {getIn(
                                                                                props.errors,
                                                                                `qualifications.${index}.institution`
                                                                            )}
                                                                        </ValidationError>
                                                                    )}
                                                            </FormField>
                                                        )}
                                                    />
                                                    <Field
                                                        validate={
                                                            gradeValidator
                                                        }
                                                        name={`qualifications.${index}.grade`}
                                                        data-testid="grade-add-qualifications"
                                                        render={({
                                                            field,
                                                            form,
                                                        }) => (
                                                            <FormField
                                                                key={`qualifications.${index}.grade`}
                                                            >
                                                                <Label htmlFor="qualificationType">
                                                                    Grade
                                                                </Label>
                                                                <Input
                                                                    type="text"
                                                                    onChange={
                                                                        props.handleChange
                                                                    }
                                                                    onBlur={
                                                                        props.handleBlur
                                                                    }
                                                                    value={
                                                                        props
                                                                            .values
                                                                            .qualifications[
                                                                            index
                                                                        ].grade
                                                                    }
                                                                    name={`qualifications.${index}.grade`}
                                                                    id="courseTitle"
                                                                    error={
                                                                        props
                                                                            .touched
                                                                            .qualifications &&
                                                                        getIn(
                                                                            props.errors,
                                                                            `qualifications.${index}.grade`
                                                                        )
                                                                    }
                                                                />

                                                                {props.touched
                                                                    .qualifications &&
                                                                    getIn(
                                                                        props.errors,
                                                                        `qualifications.${index}.grade`
                                                                    ) && (
                                                                        <ValidationError>
                                                                            {getIn(
                                                                                props.errors,
                                                                                `qualifications.${index}.grade`
                                                                            )}
                                                                        </ValidationError>
                                                                    )}
                                                            </FormField>
                                                        )}
                                                    />
                                                    <Field
                                                        validate={
                                                            yearOfQualificationValidator
                                                        }
                                                        name={`qualifications.${index}.yearOfQualification`}
                                                        data-testid="year-of-qual-add-qualifications"
                                                        render={({
                                                            field,
                                                            form,
                                                        }) => (
                                                            <FormField
                                                                key={`qualifications.${index}.yearOfQualification`}
                                                            >
                                                                <Label htmlFor="qualificationType">
                                                                    Year
                                                                    completed
                                                                </Label>
                                                                <Input
                                                                    type="text"
                                                                    onChange={
                                                                        props.handleChange
                                                                    }
                                                                    onBlur={
                                                                        props.handleBlur
                                                                    }
                                                                    value={
                                                                        props
                                                                            .values
                                                                            .qualifications[
                                                                            index
                                                                        ]
                                                                            .yearOfQualification
                                                                    }
                                                                    name={`qualifications.${index}.yearOfQualification`}
                                                                    id="courseTitle"
                                                                    error={
                                                                        props
                                                                            .touched
                                                                            .qualifications &&
                                                                        getIn(
                                                                            props.errors,
                                                                            `qualifications.${index}.yearOfQualification`
                                                                        )
                                                                    }
                                                                />

                                                                {props.touched
                                                                    .qualifications &&
                                                                    getIn(
                                                                        props.errors,
                                                                        `qualifications.${index}.yearOfQualification`
                                                                    ) && (
                                                                        <ValidationError>
                                                                            {getIn(
                                                                                props.errors,
                                                                                `qualifications.${index}.yearOfQualification`
                                                                            )}
                                                                        </ValidationError>
                                                                    )}
                                                            </FormField>
                                                        )}
                                                    />
                                                    {index > 0 && (
                                                        <RemoveButton
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                arrayHelpers.remove(
                                                                    index
                                                                );
                                                                // required to stop Formik validating
                                                                // untouched subjects
                                                                return props.setFieldTouched(
                                                                    "qualifications",
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            Remove qualification
                                                        </RemoveButton>
                                                    )}
                                                    <HorizontalDivider />
                                                </>
                                            )
                                        )}
                                        <PrimaryCTAButton
                                            type="button"
                                            disabled={hasEmptyField(
                                                _last(
                                                    props.values.qualifications
                                                )
                                            )}
                                            onClick={e => {
                                                e.preventDefault();
                                                arrayHelpers.push(
                                                    emptyQualification
                                                );
                                                return props.setFieldTouched(
                                                    "qualifications",
                                                    false
                                                );
                                            }}
                                        >
                                            Add another
                                        </PrimaryCTAButton>
                                    </>
                                )}
                            />
                        ) : null}
                        <RadioGroup>
                            <div>
                                <Field
                                    validate={
                                        hasAdditionalQualificationsValidator
                                    }
                                    component={RadioButton}
                                    name="hasAdditionalQualifications"
                                    id="no"
                                    data-testid="additionalQualificationsRadioNo"
                                    label="No"
                                />
                            </div>
                        </RadioGroup>
                        {props.errors.hasAdditionalQualifications && (
                            <ValidationError>
                                {props.errors.hasAdditionalQualifications}
                            </ValidationError>
                        )}
                        <PrimaryCTAButton
                            type="submit"
                            disabled={isSubmitting}
                            data-testid="next-button"
                        >
                            Next →
                        </PrimaryCTAButton>
                    </Form>
                )}
            />
        </PageContainer>
    );
};

export default AdditionalQualifications;
